// Port of Location/LocationFixConverter.swift: browser positions → TrackPoint, dropping or nulling invalid parts.
// - negative or non-finite horizontal accuracy, or an invalid coordinate → the fix is dropped (undefined);
// - negative/missing vertical accuracy or non-finite/missing altitude → elevation and verticalAccuracy undefined;
// - negative, non-finite or missing speed / course → undefined.
// Note: the Geolocation API defines `altitude` above the WGS84 ellipsoid (what Android Chrome reports, about 50 m
// above sea level in the Alps), while iOS Safari passes Core Location's altitude above sea level. No geoid correction
// is applied: the browser does not say which one it gives.

import type { EpochMs, TrackPoint } from '../core/models';
import { isValidCoordinate } from '../core/geo';

export interface RawFix {
  latitude: number;
  longitude: number;
  altitude: number;
  horizontalAccuracy: number;
  verticalAccuracy: number;
  speed: number;
  course: number;
  timestamp: EpochMs;
}

/** The part of `GeolocationPosition` read (so tests can pass plain objects). */
export interface GeolocationPositionLike {
  readonly coords: {
    readonly latitude: number;
    readonly longitude: number;
    readonly altitude: number | null;
    readonly accuracy: number;
    readonly altitudeAccuracy: number | null;
    readonly heading: number | null;
    readonly speed: number | null;
  };
  readonly timestamp: EpochMs;
}

function validNonNegative(value: number): number | undefined {
  return Number.isFinite(value) && value >= 0 ? value : undefined;
}

export const LocationFixConverter = {
  trackPoint(fix: RawFix): TrackPoint | undefined {
    if (!Number.isFinite(fix.horizontalAccuracy) || fix.horizontalAccuracy < 0) return undefined;
    const coordinate = { latitude: fix.latitude, longitude: fix.longitude };
    if (!isValidCoordinate(coordinate)) return undefined;
    const hasVertical =
      Number.isFinite(fix.verticalAccuracy) && fix.verticalAccuracy >= 0 && Number.isFinite(fix.altitude);
    const point: TrackPoint = { coordinate, timestamp: fix.timestamp, horizontalAccuracy: fix.horizontalAccuracy };
    if (hasVertical) {
      point.elevation = fix.altitude;
      point.verticalAccuracy = fix.verticalAccuracy;
    }
    const speed = validNonNegative(fix.speed);
    if (speed !== undefined) point.speed = speed;
    const course = validNonNegative(fix.course);
    if (course !== undefined) point.course = course;
    return point;
  },

  /** A `GeolocationPosition` (null members are "unknown", like CoreLocation's -1 sentinels). */
  fromPosition(position: GeolocationPositionLike): TrackPoint | undefined {
    const c = position.coords;
    return LocationFixConverter.trackPoint({
      latitude: c.latitude,
      longitude: c.longitude,
      altitude: c.altitude ?? Number.NaN,
      horizontalAccuracy: c.accuracy,
      verticalAccuracy: c.altitudeAccuracy ?? -1,
      speed: c.speed ?? -1,
      course: c.heading ?? -1,
      timestamp: position.timestamp,
    });
  },

  /**
   * Heading in degrees: true heading when valid, else magnetic; undefined when the reading is invalid (negative
   * accuracy) or neither value is valid.
   */
  headingDegrees(trueHeading: number, magneticHeading: number, accuracy: number): number | undefined {
    if (!Number.isFinite(accuracy) || accuracy < 0) return undefined;
    const t = validNonNegative(trueHeading);
    if (t !== undefined) return t % 360;
    const m = validNonNegative(magneticHeading);
    if (m !== undefined) return m % 360;
    return undefined;
  },
};
